import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction,
} from 'mobx';
import {
  SkillLevel,
  UpdateSkillLevelsRequest,
} from '../apiClients/workspace/types';
import { workspaceApi } from '../apiClients/workspace/workspaceApi';
import i18n from '../i18n';
import { getDefaultIconIdByWeight } from '../lib/userUtils';
import { notificationStore } from './NotificationStore';

export class SkillLevelsStore {
  @observable skillLevels: Map<number, SkillLevel> = new Map();
  @observable isLoading = false;
  @observable error: Error | null = null;
  @observable isSaving = false;
  @observable saveError: Error | null = null;

  constructor() {
    makeObservable(this);
  }

  // ===============================
  // COMPUTED PROPERTIES
  // ===============================

  @computed get hasData(): boolean {
    return this.skillLevels.size > 0;
  }

  @computed get isError(): boolean {
    return this.error !== null;
  }

  @computed get levelsArray(): SkillLevel[] {
    return Array.from(this.skillLevels.values()).sort(
      (a, b) => a.weight - b.weight
    );
  }

  @computed get hasUnsavedChanges(): boolean {
    // This could be extended to track actual changes
    return false;
  }

  // ===============================
  // ACTIONS
  // ===============================

  @action async loadSkillLevels(): Promise<void> {
    if (this.isLoading) {
      return;
    }

    this.isLoading = true;
    this.error = null;

    try {
      const levels = await workspaceApi.getSkillLevels();
      runInAction(() => {
        this.skillLevels.clear();
        levels.forEach((level) => {
          this.skillLevels.set(level.weight, level);
        });
      });
    } catch (error) {
      runInAction(() => {
        this.error = error as Error;
      });
      notificationStore.showError(i18n.t('errors.failedToLoadSkillLevels'));
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  @action async updateSkillLevels(
    request: UpdateSkillLevelsRequest
  ): Promise<SkillLevel[] | null> {
    this.isSaving = true;
    this.saveError = null;

    try {
      const response = await workspaceApi.updateSkillLevels(request);

      // Update local state with the response
      runInAction(() => {
        this.skillLevels.clear();
        response.forEach((level) => {
          this.skillLevels.set(level.weight, level);
        });
      });

      notificationStore.showSuccess(i18n.t('skillLevels.saveSuccess'));
      return response;
    } catch (error) {
      runInAction(() => {
        this.saveError = error as Error;
      });
      notificationStore.showError(i18n.t('skillLevels.saveError'));
      console.error(error);
      return null;
    } finally {
      runInAction(() => {
        this.isSaving = false;
      });
    }
  }

  @action clearError(): void {
    this.error = null;
    this.saveError = null;
  }

  // ===============================
  // GETTERS
  // ===============================

  getSkillLevelByWeight(weight: number): SkillLevel | undefined {
    return this.skillLevels.get(weight);
  }

  getSkillLevelName(weight: number): string | null {
    const level = this.skillLevels.get(weight);
    return level?.name || null;
  }

  getSkillLevelIconId(weight: number): string {
    const level = this.skillLevels.get(weight);
    return level?.settings?.iconId || getDefaultIconIdByWeight(weight);
  }

  // ===============================
  // VALIDATION HELPERS
  // ===============================

  validateLevels(levels: SkillLevel[]): string[] {
    const errors: string[] = [];

    // Check for duplicate icon IDs
    const iconCounts = new Map<string, number>();
    levels.forEach((level) => {
      const iconId =
        level.settings?.iconId || getDefaultIconIdByWeight(level.weight);
      iconCounts.set(iconId, (iconCounts.get(iconId) || 0) + 1);
    });

    const duplicateIcons = Array.from(iconCounts.entries())
      .filter(([, count]) => count > 1)
      .map(([iconId]) => iconId);

    if (duplicateIcons.length > 0) {
      errors.push(i18n.t('skillLevels.duplicateIconsError'));
    }

    // Check for empty names
    const emptyNames = levels.filter((level) => !level.name.trim());
    if (emptyNames.length > 0) {
      errors.push(i18n.t('skillLevels.emptyNamesError'));
    }

    return errors;
  }

  hasIconConflict(levels: SkillLevel[], targetWeight: number): boolean {
    const targetLevel = levels.find((l) => l.weight === targetWeight);
    if (!targetLevel) return false;

    const targetIconId =
      targetLevel.settings?.iconId || getDefaultIconIdByWeight(targetWeight);
    return levels.some(
      (l) =>
        l.weight !== targetWeight &&
        (l.settings?.iconId || getDefaultIconIdByWeight(l.weight)) ===
          targetIconId
    );
  }

  getUsedIconIds(levels: SkillLevel[], excludeWeight: number): string[] {
    return levels
      .filter((l) => l.weight !== excludeWeight)
      .map((l) => l.settings?.iconId || getDefaultIconIdByWeight(l.weight));
  }

  // ===============================
  // UTILITY METHODS
  // ===============================

  @action createDefaultLevels(): SkillLevel[] {
    const defaultLevels: SkillLevel[] = [];

    for (let i = 1; i <= 5; i++) {
      const existingLevel = this.skillLevels.get(i);
      if (existingLevel) {
        defaultLevels.push(existingLevel);
      } else {
        // Create default level
        defaultLevels.push({
          workspaceId: '', // Will be set by backend
          name: i18n.t(`skillLevels.defaultLevel${i}`),
          weight: i,
          settings: { iconId: getDefaultIconIdByWeight(i) },
          createdAt: new Date().toISOString(),
        });
      }
    }

    return defaultLevels;
  }

  @action refresh(): Promise<void> {
    return this.loadSkillLevels();
  }
}

export const skillLevelsStore = new SkillLevelsStore();
